<template>
  <v-form ref="form" class="form">
    <v-row>
      <v-col class="py-0" cols="12" sm="4">
        <v-autocomplete
          v-model="document._folder.id"
          :items="folders"
          item-text="name"
          item-value="id"
          :label="$t('documents.folder')"
          :loading="resourceLoading"
          :rules="
            resourceLoading
              ? []
              : [rules.required, backEndValidation('folder_id')]
          "
        ></v-autocomplete>
      </v-col>
      <v-col class="py-0" cols="12" sm="4">
        <v-autocomplete
          v-model="document.status"
          :items="statuses"
          :label="$t('documents.status')"
          item-text="name"
          item-value="id"
          :loading="resourceLoading"
          :rules="resourceLoading ? [] : [backEndValidation('status')]"
        ></v-autocomplete>
      </v-col>
      <v-col class="py-0" cols="12" sm="4">
        <v-checkbox
          :disabled="!this.draft"
          v-model="document.draft"
          color="primary"
          :label="$t('documents.draft')"
          class="checkbox"
        ></v-checkbox>
      </v-col>
      <v-col class="py-0" cols="12">
        <v-text-field
          v-model="document.name"
          :label="$t('documents.name')"
          :loading="resourceLoading"
          :rules="
            resourceLoading ? [] : [rules.required, backEndValidation('name')]
          "
        ></v-text-field>
      </v-col>
    </v-row>
    <v-divider class="my-5" />
    <v-row>
      <v-col class="py-0" cols="12" sm="4">
        <v-autocomplete
          :disabled="!!document.id"
          v-model="type_id"
          :items="types"
          :label="$t('documents.type')"
          item-text="name"
          item-value="id"
          :loading="resourceLoading"
          :rules="
            resourceLoading
              ? []
              : [rules.required, backEndValidation('document_type')]
          "
        ></v-autocomplete>
      </v-col>
      <v-col class="py-0" cols="12" sm="4">
        <v-select
          :disabled="!document.draft"
          v-model="document._template.id"
          :items="templates"
          :label="$t('documents.template')"
          item-text="name"
          item-value="id"
          :loading="resourceLoading"
          :rules="
            resourceLoading
              ? []
              : [rules.required, backEndValidation('template')]
          "
        ></v-select>
      </v-col>
      <v-col class="py-0" cols="12" sm="4">
        <v-autocomplete
          :disabled="!document.draft"
          v-model="document.language"
          :items="languages"
          :label="$t('documents.language')"
          :loading="resourceLoading"
        ></v-autocomplete>
      </v-col>
    </v-row>
    <v-divider class="my-5" />
    <v-row>
      <v-col class="py-0" cols="12" sm="4">
        <AutocompleteContacts
          :disabled="!document.draft"
          :item="receiver_id"
          :updated="updateReceiver"
          label="documents.receiver"
          :loading="resourceLoading"
        ></AutocompleteContacts>
      </v-col>
      <v-col class="py-0" cols="12" sm="4">
        <AutocompleteContacts
          :disabled="!document.draft"
          :item="creditor_id"
          :updated="updateCreditor"
          label="documents.creditor"
          :loading="resourceLoading"
        ></AutocompleteContacts>
      </v-col>
      <v-col class="py-0" cols="12" sm="4">
        <AutocompleteContacts
          :disabled="!document.draft"
          :item="sender_id"
          :updated="updateSender"
          label="documents.sender"
          :loading="resourceLoading"
        ></AutocompleteContacts>
      </v-col>
    </v-row>
    <v-divider class="my-5" />
    <v-row>
      <v-col class="py-0" cols="12">
        <v-textarea
          v-model="document.notices"
          :label="$t('documents.notices')"
          :loading="resourceLoading"
          :rules="resourceLoading ? [] : [backEndValidation('notices')]"
        ></v-textarea>
      </v-col>
    </v-row>
    <v-row>
      <v-col class="py-0" cols="12" sm="6">
        <v-btn color="primary mr-3 mb-3" v-on:click="save()" :loading="loading">
          <v-icon left>{{ $props.button_icon }}</v-icon>
          {{ $t(`documents.${$props.button_title}`) }}
        </v-btn>
        <v-btn color="primary mr-3 mb-3" v-on:click="exportPDF()">
          <v-icon left>mdi-download-outline</v-icon>
          {{ $t(`documents.export`) }}
        </v-btn>
      </v-col>
      <v-col class="py-0" cols="12" sm="3" offset-sm="3">
        <v-switch
          class="mt-1"
          color="primary"
          v-model="showPreview"
          :label="$t(`documents.preview`)"
          :disabled="!document.draft"
        >
        </v-switch>
      </v-col>
    </v-row>
    <v-divider class="my-5" />

    <FieldsEditor
      v-if="template"
      v-model="document.field_contents"
      :template="template"
      :locale="this.document.language"
      :localization="localization"
      :static-view="previewHtml"
      @input="
        () => {
          this.fetchPreviewData();
        }
      "
      ref="render"
      class="document-render"
    />
  </v-form>
</template>

<script>
import { debounce } from "lodash";
import { rules } from "./../mixins/rules";
import FieldsEditor from "./FieldsEditor";
import AutocompleteContacts from "../common/inputs/AutocompleteContacts";
export default {
  name: "FormDocument",
  mixins: [rules],
  components: { FieldsEditor, AutocompleteContacts },
  data: () => ({
    showPreview: false,
    previewHtml: undefined,
    loading: false,
    documentHash: "",
    draft: true
  }),
  props: {
    dispatch: String,
    button_title: String,
    button_icon: String,
    document: {
      default: function() {
        return {
          _folder: { id: null },
          _template: {
            id: null,
            _type: { id: null }
          },
          _receiver: { id: null },
          _creditor: { id: null },
          _sender: { id: null },
          draft: true
        };
      }
    },
    resourceLoading: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    statuses() {
      return (
        ((this.document && this.document._template._type) || {}).statuses || []
      );
    },
    type_id: {
      get() {
        return this.document._template._type.id;
      }
    },
    receiver_id: {
      get: function() {
        return this.document &&
          this.document._receiver &&
          this.document._receiver.id
          ? this.document._receiver.id
          : null;
      },
      set: function(value) {
        this.document._receiver = { id: value };
      }
    },
    creditor_id: {
      get() {
        return this.document &&
          this.document._creditor &&
          this.document._creditor.id
          ? this.document._creditor.id
          : null;
      },
      set(value) {
        this.document._creditor = { id: value };
      }
    },
    sender_id: {
      get() {
        return this.document &&
          this.document._sender &&
          this.document._sender.id
          ? this.document._sender.id
          : null;
      },
      set(value) {
        this.document._sender = { id: value };
      }
    },
    folders: {
      get() {
        return this.$store.getters["folderStore/getList"].filter(a =>
          a._permissions.find(p => p === "folder.read")
        );
      }
    },
    languages: {
      get() {
        return Object.keys(this.template.localization || {});
      }
    },
    localization: {
      get() {
        return (
          (this.template.localization &&
            this.template.localization[this.document.language]) ||
          {}
        );
      }
    },
    template: {
      get() {
        return this.$store.getters["documentTemplateStore/getItem"];
      }
    },
    templates: {
      get() {
        return this.$store.getters["documentTemplateStore/getList"].filter(
          i => i.type === this.type_id
        );
      }
    },
    types: {
      get() {
        return this.$store.getters["documentTypeStore/getList"];
      }
    },
    type() {
      if (this.type_id) {
        return this.types.find(e => e.id === this.type_id) || {};
      }
      return {};
    }
  },
  methods: {
    updatePreviewData() {
      if (
        this.document.id &&
        !this.resourceLoading &&
        JSON.stringify(this.document) !== this.documentHash
      ) {
        this.fetchPreviewData();
      }
    },
    // this returns the document instance with changes previewed, but not stored
    fetchPreviewData(mergeData = {}) {
      this.loading = true;
      let item = { ...this.document, ...mergeData };
      this.$store
        .dispatch("documentStore/previewData", {
          item,
          errorHandler: this.errorHandler.bind(this, () => {
            this.$refs.form.validate();
          }),
          onSuccess: document => {
            if (document.draft === false) {
              this.draft = false;
            } else {
              document.draft = this.document.draft;
            }
            this.documentHash = JSON.stringify(document);
            this.document = document;
          }
        })
        .finally(() => (this.loading = false));
    },
    async fetchHtmlPreview() {
      const response = await this.$store.dispatch("documentStore/previewHTML", {
        item: this.document
      });
      return response.data;
    },
    exportPDF() {
      this.save(() => {
        this.$store.dispatch("documentStore/exportPDF", {
          item: this.document
        });
      });
    },
    save(callback = () => {}) {
      this.clearBackEndErrors();
      if (this.$refs.form.validate()) {
        this.loading = true;
        let item = {
          ...this.document
        };

        this.$store
          .dispatch(this.$props.dispatch, {
            item,
            errorHandler: this.errorHandler.bind(this, () => {
              this.$refs.form.validate();
            }),
            onSuccess: () => {
              callback();
            }
          })
          .finally(() => (this.loading = false));
      }
    },
    updateCreditor(val) {
      this.creditor_id = val;
    },
    updateSender(val) {
      this.sender_id = val;
    },
    updateReceiver(val) {
      this.receiver_id = val;
    }
  },
  watch: {
    document: {
      handler: debounce(function() {
        if (this?.$refs?.form && this.document.draft) {
          // if the form was already destroyed, we can't update
          // and if the document is not a draft, it doesn't make sense to update
          this.showPreview = false;
          this.updatePreviewData();
        }
      }, 1000),
      deep: true
    },
    "document.draft": function(draft) {
      if (this.document.id) {
        this.showPreview = !draft;
      }
    },
    "document._template.id": function(id) {
      if (id) {
        this.$store.dispatch("documentTemplateStore/get", { id });
      }
    },
    showPreview: function(preview) {
      if (!this.document.id) {
        return;
      }
      if (!preview) {
        this.previewHtml = undefined;
      } else {
        this.fetchHtmlPreview().then(html => {
          this.previewHtml = html;
        });
      }
    }
  },
  mounted() {
    setTimeout(this.updatePreviewData, 500);
    this.$store.dispatch("folderStore/getList", {
      params: { limit: -1 }
    });
    this.$store.dispatch("documentTypeStore/getList", {
      params: { limit: -1 }
    });
    this.$store.dispatch("documentTemplateStore/getList", {
      params: { limit: -1 }
    });
  }
};
</script>
<style>
.v-card.preview-dialog .v-card__text {
  padding: 0 !important;
  line-height: 0 !important;
}
.document-render {
  height: calc(100vh - 100px);
}
.a4 {
  width: 21cm;
  height: 60vh;
}
.page {
  overflow: auto;
}
</style>
